// MUI から Button、Grid コンポーネントをインポート
import { Button, Grid } from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack5';
// React とフックをインポート
import React, { useEffect, useState } from 'react';
// react-hook-form から useForm フックをインポート
import { useForm } from 'react-hook-form';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
import { useCalendarApi } from 'src/api/useCalendarApi';
import { FetchPermissionProps, usePermissionApi } from 'src/api/usePermissionApi';
import { HeaderBreadcrumbsProps } from 'src/components/HeaderBreadcrumbs';
import { PageLayout } from 'src/components/app-components/PageLayout';
import { StyledLoading } from 'src/components/app-components/StyledLoading';
import { CREATE_USER_SYSTEM, REMINDER_STATUS } from 'src/constants';
import { usePermission } from 'src/hooks/usePermission';
import { setPermission } from 'src/redux/slices/pageSlice';
// Redux ストアセレクターをインポート
import useAuth from 'src/hooks/useAuth';
import { RootState, useAppSelector, useDispatch } from 'src/redux/store';
import { CALENDAR_EDIT_TAB_VALUE, TabValue } from '../constants';
import useCalendarLayout from '../hooks/useCalendarLayout';
import { useReminderListGrid } from '../hooks/useReminderListGrid';
import { ReminderListGrid } from '../pages/list/ReminderListGrid';
import { enqueteCalendarSlice, setTabValue } from '../store/enqueteCalendarSlice';
import { CalendarForm } from './CalendarForm';

// プロパティインターフェースを定義
export interface CalendarCreateLayoutProps {
  // カレンダーID
  id: string | undefined;
  // ページタイトル
  pageTitle: string;
  // breadCrumbs
  breadCrumbs: HeaderBreadcrumbsProps;
}
// コンプライアンス・レビュー カレンダー予定登録画面の全体を管理するコンポーネント。
export const CalendarCreateLayout: React.FC<CalendarCreateLayoutProps> = (props) => {
  // パラメータ
  const { id, pageTitle, breadCrumbs } = props;
  // 翻訳関数を取得
  const { t } = useTranslation();

  // Redux dispatch 関数を取得
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  // react-hook-form のメソッド
  const methods = useForm({
    mode: 'onChange',
  });
  // use hooks
  const { isLoading, initializeStore, getCalendarData, onClickSaveData } = useCalendarLayout();
  // リマインドデータ取得
  const { getReminderList } = useReminderListGrid();
  const { checkPermission } = usePermissionApi();
  const { getConfirmData } = useCalendarApi();
  // Redux ストアからカレンダーデータを取得
  const calendarData = useAppSelector((state: RootState) => state.enqueteCalendar.calendarData);
  const isConfirmor = useAppSelector((state: RootState) => state.enqueteCalendar.isConfirmor);
  // Redux ストアからリマインドデータを取得
  const reminderListData = useAppSelector((state: RootState) => state.enqueteCalendar.reminderListData);
  // 権限フックを使用（各種ロールフラグを取得）
  const { isAdmin, isViewerAdmin, isSurveyOwner, isAnswerEditor, isAnswerReader, isRespondentPerson, isRespondentManager } = usePermission();
  const [isDisabled, setIsDisabled] = useState(false);

  // 初期
  useEffect(() => {
    if (id != undefined) {
      // カレンダーデータ取得
      getCalendarData(id);
      // リマインドデータ取得
      getReminderList(id);
    }
    return () => {
      // 初期化処理
      initializeStore();
    };
  }, []);

  useEffect(() => {
    const fetchPermission = async () => {
      // アンケートの新規作成時等は権限のチェックは不要なのでIDが存在しない場合は行わない
      if (calendarData?.surveyDetailId) {
        // アンケート詳細Id
        const permissionParam: FetchPermissionProps = {
          surveyDetailId: calendarData.surveyDetailId,
          respondentTargetId: calendarData.respondentTargetId,
        };
        // アンケートに対する権限を持っているか確認
        const result = await checkPermission(permissionParam);
        // アンケートに対する権限設定
        dispatch(setPermission(result));
      }
      if (calendarData?.respondentTargetId) {
        const calendarEntity = await getConfirmData(calendarData.respondentTargetId);
        const isConfirmor = !!calendarEntity;
        dispatch(enqueteCalendarSlice.actions.setIsConfirmor(isConfirmor));
      } else {
        dispatch(enqueteCalendarSlice.actions.setIsConfirmor(false));
      }
    };

    // カレンダーデータ取得
    if (id != undefined) {
      fetchPermission();
    }
  }, [calendarData?.surveyDetailId]);
  const { user } = useAuth();
  useEffect(() => {
    const isSystemCreated = calendarData?.createdBy === CREATE_USER_SYSTEM;
    const isCreatedByMe = calendarData.createdBy === user?.email;

    // 共有権限
    const hasPermission =
      isSurveyOwner || // 管理者
      isAdmin || // admin
      isAnswerEditor || // 本部極担当者（編集）
      isRespondentPerson || // 回答者
      isConfirmor || // 確認者
      !!calendarData?.reminderUserId; // リマインドの宛先

    // 手動権限
    const hasManualPermission =
      isAnswerReader || // 本部極担当者（参照）
      isRespondentManager || // 依頼先管理者
      (!calendarData?.id && !isViewerAdmin) || // 新規作成
      (isCreatedByMe && !isViewerAdmin); // 個人作成

    const canEdit = isSystemCreated ? hasPermission : hasPermission || hasManualPermission;

    setIsDisabled(!canEdit || (isViewerAdmin && !calendarData?.id));
  }, [calendarData, isAdmin, isViewerAdmin, isSurveyOwner, isAnswerEditor, isAnswerReader, isRespondentPerson, isConfirmor]);

  // 保存処理
  const onClickSave = async () => {
    // 検証対象のすべてのフィールド
    const allFields = ['titleJpn', 'titleEng', 'startDate', 'endDate', 'descriptionJpn', 'descriptionEng'];
    // 自動生成時およびViewerAdmin時に、検証が不要なフィールド
    const isExcludeCalendarFields = calendarData?.createdBy === CREATE_USER_SYSTEM || isViewerAdmin;
    const fields = isExcludeCalendarFields
      ? calendarData?.titleJpn === '現地現物確認予定日'
        ? ['descriptionJpn', 'descriptionEng']
        : []
      : allFields;
    const valid = fields.length === 0 || (await methods.trigger(fields));
    if (!valid) {
      // 期間開始日
      const startDateErr = await methods.getFieldState('startDate')?.error;
      // 期間終了日
      const endDateErr = await methods.getFieldState('endDate')?.error;
      // 見出し（日本語）
      const titleJpnErr = await methods.getFieldState('titleJpn')?.error;
      // 見出し（英語）
      const titleEngErr = await methods.getFieldState('titleEng')?.error;
      // 本文（日本語）
      const descriptionJpnErr = await methods.getFieldState('descriptionJpn')?.error;
      // 本文（英語）
      const descriptionEngErr = await methods.getFieldState('descriptionEng')?.error;
      // 開始日と終了日エラーなし
      if (!startDateErr && !endDateErr) {
        // 日本語タブエラーあり、英語タブエラーなし
        if ((titleJpnErr || descriptionJpnErr) && !titleEngErr && !descriptionEngErr) {
          // 日本語タブ表示
          dispatch(setTabValue(CALENDAR_EDIT_TAB_VALUE.JAPANESE as TabValue));
        }
        // 日本語タブエラーなし、英語タブエラーあり
        if (!titleJpnErr && !descriptionJpnErr && (titleEngErr || descriptionEngErr)) {
          // 英語タブ表示
          dispatch(setTabValue(CALENDAR_EDIT_TAB_VALUE.ENGLISH as TabValue));
        }
      }
      // エラーある
      return;
    }
    const titleJpn = methods.getValues('titleJpn');
    const titleEng = methods.getValues('titleEng');
    if (titleJpn === '' && titleEng === '') {
      enqueueSnackbar(t('CalendarCreate.validateError.titleRequired'), { variant: 'error' });
      return;
    }

    const descriptionJpn = methods.getValues('descriptionJpn');
    const descriptionEng = methods.getValues('descriptionEng');
    // リマインドコ-ザ-の件数>0のチック
    if (descriptionJpn === '' && descriptionEng === '' && reminderListData.length > 0) {
      enqueueSnackbar(t('CalendarCreate.validateError.textRequired'), { variant: 'error' });
      return;
    }

    // 期間終了日
    const endDate = new Date(methods.getValues('endDate'));
    // 重複Map
    const seen = new Map();
    // 重複フラグ
    let hasDuplicate = false;
    // 終了日フラグ
    let endDtFlag = false;

    // 明細件数あり場合、チェック実行
    if (reminderListData.length > 0) {
      reminderListData.forEach((item) => {
        // キー：「リマインドのメールアドレス」と「送信日付」
        const key = `${item.reminderUserId}_${item.sentAt}`;
        // 重複チェック
        if (seen.has(key)) {
          hasDuplicate = true;
        } else {
          seen.set(key, item);
        }
        // リマインドステータス：送信待ち(active)
        if (item.status === REMINDER_STATUS.ACTIVE) {
          // リマインド日付
          if (item.sentAt !== '' && item.sentAt !== undefined) {
            const sentAt = new Date(item.sentAt);
            // リマインド日付 > 適用期間終了日
            if (new Date(format(sentAt, 'yyyy/MM/dd')) > new Date(format(endDate, 'yyyy/MM/dd'))) {
              endDtFlag = true;
            }
          }
        }
      });
    }
    if (hasDuplicate) {
      // エラーメッセージ： リマインドに重複が存在する。
      enqueueSnackbar(t('CalendarCreate.message.duplicateMsg'), { variant: 'error' });
    }
    if (endDtFlag) {
      // エラーメッセージ： リマインド日付は適用期間終了日よりも前に設定してください
      enqueueSnackbar(t('CalendarCreate.message.varidationErrorToIsValidReminderDate'), { variant: 'error' });
    }
    // エラーあり、処理終了。
    if (hasDuplicate || endDtFlag) {
      return;
    }
    // レンダー情報とリマインド情報を保存する
    await onClickSaveData(calendarData, reminderListData);
  };

  if (isLoading) {
    return (
      <>
        {/** ローディング */}
        <StyledLoading isOpen={isLoading} />
      </>
    );
  }

  return (
    <PageLayout title={pageTitle} breadcrumbs={breadCrumbs}>
      {/* カレンダー詳細フォーム */}
      <CalendarForm methods={methods} />
      {/* リマインド一覧 */}
      <ReminderListGrid />
      {/* 保存ボタン */}
      <Grid container item xs={12} justifyContent="flex-end">
        <Button
          // 塗りつぶし
          variant={'contained'}
          onClick={() => {
            onClickSave();
          }}
          children={t('CalendarCreate.button.save')}
          sx={{ ml: 3, mt: 1 }}
          type="submit"
          disabled={isDisabled}
        />
      </Grid>
    </PageLayout>
  );
};
